/**
 * ScheduleRunHistory
 *
 * Paginated run history panel opened via [执行历史] button on the schedule card.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { RunStatusBadge } from './RunStatusBadge';
import { ScheduleRunDetail } from './ScheduleRunDetail';
import { useScheduleRuns } from '../hooks/useSchedules';
import { formatDuration } from '@/lib/format/duration';
import type { ScheduleRunSummary } from '@/types/schedule';

interface Props {
  scheduleId: string;
}

const PAGE_SIZE = 10;

function formatFiredAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ScheduleRunHistory({ scheduleId }: Props) {
  const [offset, setOffset] = useState(0);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const { data, isLoading } = useScheduleRuns(scheduleId, { limit: PAGE_SIZE, offset });

  const runs = data?.runs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const toggleExpand = (runId: string) => {
    setExpandedRunId((prev) => (prev === runId ? null : runId));
  };

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-gray-500">加载中...</div>;
  }

  if (runs.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-500">暂无执行记录</div>;
  }

  return (
    <div className="space-y-2">
      {runs.map((run: ScheduleRunSummary) => (
        <div key={run.run_id} className="border border-gray-100 rounded-lg overflow-hidden">
          {/* Run row */}
          <button
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            onClick={() => toggleExpand(run.run_id)}
          >
            {expandedRunId === run.run_id ? (
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            )}
            <span className="text-sm text-gray-700 shrink-0">{formatFiredAt(run.fired_at)}</span>
            <RunStatusBadge status={run.status} />
            <span className="text-sm text-gray-600 shrink-0">
              {run.completed_users}/{run.total_users}
            </span>
            <span className="text-xs text-gray-400 shrink-0">
              {formatDuration(run.duration_seconds)}
            </span>
            {run.parent_run_id && (
              <span className="text-xs text-gray-400 italic shrink-0">重跑</span>
            )}
          </button>

          {/* Expanded detail */}
          {expandedRunId === run.run_id && (
            <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
              <ScheduleRunDetail
                scheduleId={scheduleId}
                runId={run.run_id}
                onRetried={() => setExpandedRunId(null)}
              />
            </div>
          )}
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-gray-500">
            第 {currentPage}/{totalPages} 页，共 {total} 条
          </span>
          <div className="flex gap-2">
            <button
              className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              上一页
            </button>
            <button
              className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              下一页
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
